// frontend/src/components/ModificarEventoModal.js
import React, { useState, useEffect } from 'react';
import EventoService from '../services/eventoService';
import '../style/components/CrearEventoModal.css';

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) {
        return fechaInput;
    }
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

const ModificarEventoModal = ({ isOpen, onClose, onSave, evento }) => {
    const [tiposEvento, setTiposEvento] = useState([]);
    const [eventoEdit, setEventoEdit] = useState({
        titulo: '',
        descripcion: '',
        fecha: '',
        horaInicio: '',
        horaFin: '',
        todoElDia: false,
        tipoEvento: '',
        idTramite: '',
        visibilidad: 'POR_AREA',
        canalesNotificacion: {
            whatsapp: false,
            email: false,
            inapp: true
        }
    });

    const obtenerPropEvento = (eventoActual, clave) => {
        return eventoActual?.[clave] ?? eventoActual?.extendedProps?.[clave];
    };

    const obtenerVisibilidadEvento = (eventoActual, idTramiteEvento) => {
        const visibilidad = obtenerPropEvento(eventoActual, 'visibilidad');
        if (visibilidad && String(visibilidad).trim()) {
            return String(visibilidad).trim();
        }
        return idTramiteEvento ? 'POR_AREA' : 'PUBLICO';
    };

    const formatDateInput = (valor) => {
        if (!valor) return '';

        if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
            return formatLocalFecha(valor);
        }

        if (typeof valor === 'string') {
            if (valor.includes('T')) {
                return valor.split('T')[0];
            }
            return valor;
        }

        return '';
    };

    const formatTimeInput = (valor) => {
        if (!valor) return '';

        if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
            return valor.toTimeString().slice(0, 5);
        }

        if (typeof valor === 'string') {
            if (valor.includes('T')) {
                const fechaConHora = new Date(valor);
                if (!Number.isNaN(fechaConHora.getTime())) {
                    return fechaConHora.toTimeString().slice(0, 5);
                }
            }
            return valor.length >= 5 ? valor.slice(0, 5) : valor;
        }

        return '';
    };

    const parseFechaFlexible = (valor) => {
        if (!valor) return null;
        if (valor instanceof Date && !Number.isNaN(valor.getTime())) return valor;
        if (typeof valor === 'string') {
            const normalizado = /^\d{4}-\d{2}-\d{2}$/.test(valor) ? `${valor}T00:00:00` : valor;
            const parsed = new Date(normalizado);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        return null;
    };

    const obtenerFechaEvento = (eventoActual) => eventoActual?.fecha ?? eventoActual?.date ?? eventoActual?.start ?? '';

    const obtenerHoraInicioEvento = (eventoActual, valorFecha) => {
        if (eventoActual?.horaInicio) {
            return formatTimeInput(eventoActual.horaInicio);
        }

        const fechaParseada = parseFechaFlexible(valorFecha);
        if (fechaParseada) {
            return formatTimeInput(fechaParseada);
        }

        return '';
    };

    useEffect(() => {
        if (isOpen && evento) {
            // Limpiar y cargar datos frescos
            EventoService.getTiposEvento()
                .then(response => {
                    setTiposEvento(response.data);

                    const fechaOrigen = obtenerFechaEvento(evento);
                    const horaInicioNormalizada = obtenerHoraInicioEvento(evento, fechaOrigen);
                    const idTramiteEvento = obtenerPropEvento(evento, 'idTramite') || '';

                    setEventoEdit({
                        titulo: evento.client || evento.titulo || '',
                        descripcion: evento.description || evento.descripcion || '',
                        fecha: formatDateInput(fechaOrigen),
                        horaInicio: horaInicioNormalizada,
                        horaFin: formatTimeInput(evento.horaFin || evento.end),
                        todoElDia: Boolean(evento.todoElDia),
                        tipoEvento: evento.type || evento.tipoEvento || '',
                        idTramite: idTramiteEvento,
                        visibilidad: obtenerVisibilidadEvento(evento, idTramiteEvento),
                        canalesNotificacion: {
                            whatsapp: false,
                            email: false,
                            inapp: true
                        }
                    });
                })
                .catch(error => console.error('Error al cargar tipos de evento:', error));
        }
    }, [isOpen, evento]);

    if (!isOpen || !evento) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEventoEdit(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const eventoParaEnviar = {
            ...eventoEdit,
            fecha: formatLocalFecha(eventoEdit.fecha),
            visibilidad: eventoEdit.visibilidad || (eventoEdit.idTramite ? 'POR_AREA' : 'PUBLICO'),
            canalesNotificacion: ['inapp']
        };
        
        console.log('📤 Modificando evento con canales fijos: inapp');
        console.log('📤 Datos completos del evento:', eventoParaEnviar);

        const idEvento = evento?.id ?? evento?.idEvento;
        if (typeof onSave === 'function') {
            onSave(idEvento, eventoParaEnviar);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="evento-modal-content">
                <h2>Modificar Evento</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Título</label>
                        <input type="text" name="titulo" value={eventoEdit.titulo} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label>Tipo de Evento</label>
                        <select name="tipoEvento" value={eventoEdit.tipoEvento} onChange={handleChange} required>
                            {tiposEvento.map(tipo => (
                                <option key={tipo.id} value={tipo.nombre}>{tipo.nombre}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Visibilidad</label>
                        <div className="d-flex gap-3 flex-wrap align-items-center">
                            <label className="d-flex align-items-center gap-2 mb-0">
                                <input
                                    type="radio"
                                    name="visibilidadEdit"
                                    value="PUBLICO"
                                    checked={eventoEdit.visibilidad === 'PUBLICO'}
                                    onChange={(e) => setEventoEdit(prev => ({ ...prev, visibilidad: e.target.value }))}
                                />
                                <span>Público</span>
                            </label>
                            <label className="d-flex align-items-center gap-2 mb-0">
                                <input
                                    type="radio"
                                    name="visibilidadEdit"
                                    value="PRIVADO"
                                    checked={eventoEdit.visibilidad === 'PRIVADO'}
                                    onChange={(e) => setEventoEdit(prev => ({ ...prev, visibilidad: e.target.value }))}
                                />
                                <span>Privado</span>
                            </label>
                            <label className="d-flex align-items-center gap-2 mb-0">
                                <input
                                    type="radio"
                                    name="visibilidadEdit"
                                    value="POR_AREA"
                                    checked={eventoEdit.visibilidad === 'POR_AREA'}
                                    onChange={(e) => setEventoEdit(prev => ({ ...prev, visibilidad: e.target.value }))}
                                />
                                <span>Por Área</span>
                            </label>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Fecha</label>
                        <input type="date" name="fecha" value={eventoEdit.fecha} onChange={handleChange} required />
                    </div>
                    <div className="form-group-inline">
                        <div className="form-group">
                            <label>Hora Inicio</label>
                            <input type="time" name="horaInicio" value={eventoEdit.horaInicio} onChange={handleChange} disabled={eventoEdit.todoElDia} />
                        </div>
                        <div className="form-group">
                            <label>Hora Fin</label>
                            <input type="time" name="horaFin" value={eventoEdit.horaFin} onChange={handleChange} disabled={eventoEdit.todoElDia} />
                        </div>
                    </div>
                    <div className="form-group-checkbox">
                        <input type="checkbox" id="todoElDia" name="todoElDia" checked={eventoEdit.todoElDia} onChange={handleChange} />
                        <label htmlFor="todoElDia">Todo el día</label>
                    </div>
                    <div className="form-group">
                        <label>Descripción</label>
                        <textarea name="descripcion" value={eventoEdit.descripcion} onChange={handleChange}></textarea>
                    </div>
                    <div className="form-group">
                        <label>ID de Trámite (Opcional)</label>
                        <input type="number" name="idTramite" value={eventoEdit.idTramite} onChange={handleChange} />
                    </div>
                    
                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancel">Cancelar</button>
                        <button type="submit" className="btn-save">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModificarEventoModal;
